/**
 * 生存中の NoteSaver と、未送信ドラフト回収スイープの所有権調停（ADR-041）。
 *
 * 同じドラフトを NoteSaver と recoverPendingDrafts() が同時に送ると、新規ノートの
 * POST が二重になる。ブラウザ内で生きている saver がある間は saver を所有者とし、
 * 回収スイープはその note_id を見送る。saver が終われば次のスイープで回収できる。
 */

/** 判定本体。React や module state に依存しない純粋関数としてテストする。 */
export function shouldRecoverDraft(
  noteId: string,
  liveSaverNoteIds: ReadonlySet<string>,
): boolean {
  return !liveSaverNoteIds.has(noteId);
}

/**
 * composition 中の owner が、次の owner 取得まで回収を保留すべきかを決める。
 * 自 lease より後の owner が一度でも取得済み、または別 owner がまだ生存中なら、
 * その owner が責任を引き継げるため古い lease の park を残さない。
 */
export function shouldParkDraftUntilNextOwner(
  leaseGeneration: number,
  latestGeneration: number,
  remainingOwnerCount: number,
): boolean {
  return leaseGeneration === latestGeneration && remainingOwnerCount === 0;
}

export interface DraftOwnershipLease {
  /** 新規ノートの POST 成功で local ID が server ID へ変わったときに呼ぶ。 */
  moveTo(noteId: string): void;
  /** composition 中の離脱時、次の saver またはページ再読込まで回収を保留する。 */
  parkUntilNextOwner(): void;
  /** API待機中に別の所有者が加わっていないかを、適用直前に確認する。 */
  isExclusive(): boolean;
  /** 何度呼んでも安全。 */
  release(): void;
}

/**
 * 同じ note_id の saver がライフサイクル移行中に一時的に重なる場合へ備え、Set ではなく
 * 参照数を持つ。片方の終了だけで別の生存中 saver の所有権を消さないためである。
 */
interface DraftOwnershipState {
  liveOwnerCount: number;
  latestGeneration: number;
  parkedGeneration: number | null;
}

const ownershipStates = new Map<string, DraftOwnershipState>();
let nextOwnershipGeneration = 0;

function cleanupOwnershipState(noteId: string, state: DraftOwnershipState): void {
  if (state.liveOwnerCount === 0 && state.parkedGeneration === null) {
    // 非active leaseは後から状態を変更できないため、世代を含めて安全に破棄できる。
    ownershipStates.delete(noteId);
  } else {
    ownershipStates.set(noteId, state);
  }
}

function retain(
  noteId: string,
  generation: number,
  inheritParkedDraft: boolean,
): void {
  const previous = ownershipStates.get(noteId);
  const parkedGeneration = previous?.parkedGeneration ?? null;
  ownershipStates.set(noteId, {
    liveOwnerCount: (previous?.liveOwnerCount ?? 0) + 1,
    latestGeneration: Math.max(previous?.latestGeneration ?? 0, generation),
    // 自leaseより古いcomposition parkだけを引き継ぐ。moveToした古いleaseが、
    // destinationで後から取得された新ownerのparkを解除してはいけない。
    parkedGeneration:
      inheritParkedDraft && parkedGeneration !== null && generation > parkedGeneration
        ? null
        : parkedGeneration,
  });
}

function release(noteId: string): void {
  const state = ownershipStates.get(noteId);
  if (!state) return;
  cleanupOwnershipState(noteId, {
    ...state,
    liveOwnerCount: Math.max(0, state.liveOwnerCount - 1),
  });
}

function createDraftOwnershipLease(
  noteId: string,
  inheritParkedDraft: boolean,
): DraftOwnershipLease {
  let currentId = noteId;
  let active = true;
  const generation = ++nextOwnershipGeneration;
  retain(currentId, generation, inheritParkedDraft);

  return {
    moveTo(nextId: string): void {
      if (!active || nextId === currentId) return;
      // 新 ID を先に確保し、どの瞬間にも所有者がいない隙間を作らない。
      // 世代はlease取得時のまま保つ。moveを新規取得扱いすると、destinationで既に
      // 後続ownerが生まれていても、この古いleaseが最新に見えてparkを残せてしまう。
      retain(nextId, generation, true);
      release(currentId);
      currentId = nextId;
    },
    parkUntilNextOwner(): void {
      if (!active) return;
      active = false;
      const state = ownershipStates.get(currentId);
      if (!state) return;
      const remainingOwnerCount = Math.max(0, state.liveOwnerCount - 1);
      cleanupOwnershipState(currentId, {
        ...state,
        liveOwnerCount: remainingOwnerCount,
        parkedGeneration: shouldParkDraftUntilNextOwner(
          generation,
          state.latestGeneration,
          remainingOwnerCount,
        )
          ? generation
          : state.parkedGeneration,
      });
    },
    isExclusive(): boolean {
      const state = ownershipStates.get(currentId);
      return (
        active &&
        state?.liveOwnerCount === 1 &&
        state.parkedGeneration === null &&
        generation === state.latestGeneration
      );
    },
    release(): void {
      if (!active) return;
      active = false;
      release(currentId);
    },
  };
}

export function acquireDraftOwnership(noteId: string): DraftOwnershipLease {
  // composition 中に離脱した旧 saver の保留は、新しい editor が責任を引き継いだ時点で解除する。
  return createDraftOwnershipLease(noteId, true);
}

/** 所有者がいない draft だけを回収処理が一時保有する。 */
export function tryAcquireDraftRecoveryOwnership(noteId: string): DraftOwnershipLease | null {
  const state = ownershipStates.get(noteId);
  if ((state?.liveOwnerCount ?? 0) > 0 || (state?.parkedGeneration ?? null) !== null) {
    return null;
  }
  return createDraftOwnershipLease(noteId, false);
}

/** 現時点の所有者を読み、純粋関数へ渡す薄い stateful wrapper。 */
export function shouldRecoverDraftNow(noteId: string): boolean {
  const ownedNoteIds = new Set(
    [...ownershipStates]
      .filter(([, state]) => state.liveOwnerCount > 0 || state.parkedGeneration !== null)
      .map(([ownedNoteId]) => ownedNoteId),
  );
  return shouldRecoverDraft(
    noteId,
    ownedNoteIds,
  );
}
